import { useRef } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';

/**
 * Reveal — smooth glide-in on scroll (translateY + opacity)
 *
 * y        : starting vertical offset in px  (default 36)
 * duration : transition length in seconds    (default 0.85)
 * delay    : stagger offset in seconds       (default 0)
 * ease     : cubic-bezier curve              (easeOutQuart)
 * amount   : fraction of element in view     (default 0.07)
 *
 * Respects prefers-reduced-motion: elements appear instantly.
 * Mobile note: 36 px is subtle enough on small screens;
 * no horizontal overflow because only translateY is used.
 */
export function Reveal({
  children,
  delay = 0,
  className = '',
  y = 36,
  x = 0,
  duration = 0.85,
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.07 });
  const shouldReduceMotion = useReducedMotion();

  if (shouldReduceMotion) {
    return <div ref={ref} className={className}>{children}</div>;
  }

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y, x }}
      animate={inView ? { opacity: 1, y: 0, x: 0 } : {}}
      transition={{
        duration,
        delay,
        ease: [0.25, 1, 0.5, 1],   /* easeOutQuart — smooth, natural glide */
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}
